// src/context/ThemeProvider.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, Theme, ThemeOptions } from '@mui/material';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import PaletteIcon from '@mui/icons-material/Palette';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

// Logo colors
const TB_GOLD = '#FFB300';       // Primary Gold
const TB_GOLD_DARK = '#FF8F00'; // Deep Gold / Orange
const TB_BLACK = '#0A0A0A';     // Blackout BG
const TB_DARK = '#111111';      // Dark mode BG

export type ThemeMode = 'light' | 'dark' | 'vivid' | 'blackout';

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within a ThemeProvider');
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    const storedTheme = localStorage.getItem('themeMode');
    return (storedTheme as ThemeMode) || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const toggleThemeMode = () => {
    setThemeMode(prev => {
      const modes: ThemeMode[] = ['light', 'dark', 'vivid', 'blackout'];
      const nextIndex = (modes.indexOf(prev) + 1) % modes.length;
      return modes[nextIndex];
    });
  };

  const getTheme = (mode: ThemeMode): Theme => {
    const baseOptions: ThemeOptions = {
      typography: {
        fontFamily: 'Inter, Arial, sans-serif',
        button: { 
          fontWeight: 600, 
          textTransform: 'none' as const,
          letterSpacing: '0.5px'
        },
      },
      shape: {
        borderRadius: 12
      },
      components: {
        MuiPaper: {
          styleOverrides: {
            root: {
              backdropFilter: 'blur(10px)',
              borderRadius: '12px',
              border: '1px solid',
              transition: 'all 0.3s ease',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderRadius: '8px',
              fontWeight: 600,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: (theme: Theme) => theme.shadows[4],
              },
            },
            contained: {
              boxShadow: 'none',
              '&:hover': {
                boxShadow: (theme: Theme) => theme.shadows[4],
              }
            }
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              transition: 'all 0.2s ease-in-out'
            }
          }
        },
      },
    };

    switch(mode) {
      case 'dark':
        return createTheme({
          ...baseOptions,
          palette: {
            mode: 'dark',
            primary: { 
              main: TB_GOLD, 
              dark: TB_GOLD_DARK,
              contrastText: TB_BLACK 
            },
            secondary: { 
              main: TB_GOLD_DARK,
              contrastText: '#FFFFFF' 
            },
            background: { 
              default: TB_DARK,
              paper: 'rgba(17, 17, 17, 0.85)',
            },
            text: { 
              primary: '#F8FAFC', 
              secondary: '#94A3B8' 
            },
            divider: 'rgba(255, 179, 0, 0.15)',
          },
          components: {
            ...baseOptions.components,
            MuiPaper: {
              styleOverrides: {
                root: {
                  borderColor: 'rgba(255, 179, 0, 0.12)',
                  backgroundImage: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  background: `linear-gradient(135deg, ${TB_GOLD}, ${TB_GOLD_DARK})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${TB_GOLD_DARK}, #E65100)`,
                  }
                }
              }
            },
          },
        });

      case 'vivid':
        return createTheme({
          ...baseOptions,
          palette: {
            mode: 'light',
            primary: { 
              main: TB_GOLD, 
              dark: TB_GOLD_DARK,
              contrastText: TB_BLACK 
            },
            secondary: { 
              main: TB_GOLD_DARK,
              contrastText: '#FFFFFF' 
            },
            background: { 
              default: '#F8FAFC',
              paper: 'rgba(255,255,255,0.95)',
            },
            text: { 
              primary: TB_BLACK, 
              secondary: '#64748B' 
            },
            divider: 'rgba(255, 179, 0, 0.15)',
          },
          components: {
            ...baseOptions.components,
            MuiPaper: {
              styleOverrides: {
                root: {
                  border: '1px solid rgba(255, 179, 0, 0.15)',
                  boxShadow: '0 4px 20px rgba(255, 179, 0, 0.08)',
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  background: `linear-gradient(135deg, ${TB_GOLD}, ${TB_GOLD_DARK})`,
                  color: TB_BLACK,
                  fontWeight: 700,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${TB_GOLD_DARK}, #E65100)`,
                  }
                }
              }
            },
          },
        });

      case 'blackout':
        return createTheme({
          ...baseOptions,
          palette: {
            mode: 'dark',
            primary: { 
              main: TB_GOLD, 
              dark: TB_GOLD_DARK,
              contrastText: TB_BLACK 
            },
            secondary: { 
              main: TB_GOLD_DARK,
              contrastText: TB_BLACK 
            },
            background: { 
              default: TB_BLACK,
              paper: 'rgba(10, 10, 10, 0.95)',
            },
            text: { 
              primary: '#FFFFFF', 
              secondary: '#CCCCCC' 
            },
            divider: 'rgba(255, 179, 0, 0.2)',
          },
          components: {
            ...baseOptions.components,
            MuiPaper: {
              styleOverrides: {
                root: {
                  borderColor: 'rgba(255, 179, 0, 0.2)',
                  backgroundImage: 'none',
                  boxShadow: '0 4px 30px rgba(255, 179, 0, 0.1)',
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  background: `linear-gradient(135deg, ${TB_GOLD}, ${TB_GOLD_DARK})`,
                  fontWeight: 700,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${TB_GOLD_DARK}, #E65100)`,
                    boxShadow: `0 6px 20px rgba(255, 179, 0, 0.4)`,
                  }
                }
              }
            },
          },
        });

      default: // light
        return createTheme({
          ...baseOptions,
          palette: {
            mode: 'light',
            primary: { 
              main: TB_GOLD, 
              dark: TB_GOLD_DARK,
              contrastText: '#FFFFFF' 
            },
            secondary: { 
              main: TB_GOLD_DARK,
              contrastText: '#FFFFFF' 
            },
            background: { 
              default: '#F1F5F9',
              paper: 'rgba(255,255,255,0.95)',
            },
            text: { 
              primary: TB_BLACK, 
              secondary: '#64748B' 
            },
            divider: 'rgba(255, 179, 0, 0.1)',
          },
          components: {
            ...baseOptions.components,
            MuiPaper: {
              styleOverrides: {
                root: {
                  border: '1px solid rgba(255, 179, 0, 0.1)',
                  boxShadow: '0 4px 15px rgba(255, 179, 0, 0.05)',
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  background: `linear-gradient(135deg, ${TB_GOLD}, ${TB_GOLD_DARK})`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${TB_GOLD_DARK}, #E65100)`,
                  }
                }
              }
            },
          },
        });
    }
  };

  return (
    <ThemeContext.Provider value={{ themeMode, toggleThemeMode }}>
      <MuiThemeProvider theme={getTheme(themeMode)}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const getThemeIcon = (themeMode: ThemeMode) => {
  switch(themeMode) {
    case 'light': 
      return <Brightness7Icon sx={{ color: "text.secondary" }} />;
    case 'dark': 
      return <Brightness4Icon sx={{ color: "primary.main" }} />;
    case 'vivid': 
      return <PaletteIcon sx={{ color: "primary.main" }} />;
    case 'blackout': 
      return <DarkModeIcon sx={{ color: "primary.main" }} />;
    default: 
      return <Brightness7Icon />;
  }
};

export default ThemeProvider;